<template>
    <ResponsiveDialog :value="value" @input="$emit('input', $event)" max-width="420" :header-title="$t('user.playersActivity.title')" header-class="skin-window-header--userchanger" :z-index="3500" card-class="skin-card">
        <div class="skin-window--userchanger userchanger-adaptive-text userchanger-bg">
            <v-card-text class="pb-0">
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-text-field
                        v-model="search"
                        :label="$t('user.playersActivity.searchLabel')"
                        dense
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        @input="onSearchInput"
                        @click:clear="onSearchInput('')"
                    />
                    <v-select
                        v-model="limit"
                        :items="limitOptions"
                        :label="$t('user.playersActivity.limitLabel')"
                        dense
                        hide-details
                        style="max-width: 90px; flex: none;"
                        @change="onLimitChange"
                    />
                </div>
            </v-card-text>
            <v-card-text class="pt-2">
                <div v-if="isLoading" class="text-center pa-4">
                    <v-progress-circular indeterminate color="primary"/>
                </div>
                <div v-else-if="players.length === 0" class="text-center pa-4 userchanger-adaptive-text">
                    {{ $t('user.playersActivity.noPlayersFound') }}
                </div>
                <div
                    v-else
                    class="players-activity-scroll"
                    @touchstart.passive="onTouchStart"
                    @touchend="onTouchEnd"
                >
                    <v-list dense>
                        <v-list-item v-for="p in players" :key="p.id">
                            <v-list-item-icon class="mr-3">
                                <v-icon :color="p.isOnline ? 'success' : 'grey'">mdi-circle</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>{{ p.name }}</v-list-item-title>
                                <v-list-item-subtitle>{{ relativeTime(p) }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </div>
                <div v-if="!isLoading && total > 0" class="d-flex align-center justify-space-between pt-2">
                    <v-btn icon :disabled="page <= 1" @click="goToPage(page - 1)">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <span class="text-caption">{{ $t('user.playersActivity.pageCaption', {page, pageCount, total}) }}</span>
                    <v-btn icon :disabled="page >= pageCount" @click="goToPage(page + 1)">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                </div>
            </v-card-text>
        </div>
    </ResponsiveDialog>
</template>

<script>
    // Admin prehled aktivity hracu - jen Kelghor, viz isDebugUser v NavDrawer.vue
    import {mapActions} from 'vuex';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';

    export default {
        name: 'PlayersActivityDialog',
        components: {ResponsiveDialog},
        props: {
            value: {type: Boolean, default: false},
        },
        data() {
            return {
                players: [],
                isLoading: false,
                search: '',
                limit: 10,
                limitOptions: [10, 25, 50, 100],
                page: 1,
                total: 0,
                searchDebounce: null,
                touchStartX: null,
                touchStartY: null,
            }
        },
        computed: {
            pageCount() {
                return Math.max(1, Math.ceil(this.total / this.limit));
            },
        },
        watch: {
            value(isOpen) {
                if (isOpen) {
                    this.page = 1;
                    this.load();
                }
            },
        },
        methods: {
            ...mapActions({
                loadPlayersActivity: 'user/loadPlayersActivity',
            }),
            load() {
                this.isLoading = true;
                this.loadPlayersActivity({page: this.page, limit: this.limit, search: this.search}).then((data) => {
                    this.players = data.items || [];
                    this.total = data.total || 0;
                    this.isLoading = false;
                });
            },
            onSearchInput() {
                clearTimeout(this.searchDebounce);
                this.searchDebounce = setTimeout(() => {
                    this.page = 1;
                    this.load();
                }, 350);
            },
            onLimitChange() {
                this.page = 1;
                this.load();
            },
            goToPage(page) {
                if (page < 1 || page > this.pageCount) {
                    return;
                }
                this.page = page;
                this.load();
            },
            onTouchStart(e) {
                this.touchStartX = e.changedTouches[0].clientX;
                this.touchStartY = e.changedTouches[0].clientY;
            },
            onTouchEnd(e) {
                if (this.touchStartX === null) {
                    return;
                }
                const deltaX = e.changedTouches[0].clientX - this.touchStartX;
                const deltaY = e.changedTouches[0].clientY - this.touchStartY;
                this.touchStartX = null;
                this.touchStartY = null;
                // jen prevazne horizontalni gesto - jinak by vertikalni scroll s bocnim jitterem mel sanci omylem prehodit stranku
                if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) {
                    return;
                }
                this.goToPage(deltaX < 0 ? this.page + 1 : this.page - 1);
            },
            relativeTime(p) {
                if (p.isOnline) {
                    return this.$t('user.playersActivity.online');
                }
                if (!p.lastActive) {
                    return this.$t('user.playersActivity.never');
                }
                const diffMs = Date.now() - new Date(p.lastActive).getTime();
                const minutes = Math.floor(diffMs / 60000);
                if (minutes < 60) {
                    return this.$tc('user.playersActivity.minutesAgo', minutes, {n: minutes});
                }
                const hours = Math.floor(minutes / 60);
                if (hours < 24) {
                    return this.$tc('user.playersActivity.hoursAgo', hours, {n: hours});
                }
                const days = Math.floor(hours / 24);
                return this.$tc('user.playersActivity.daysAgo', days, {n: days});
            },
        },
    }
</script>

<style scoped>
.userchanger-bg {
    display: flex;
    flex-direction: column;
    min-height: 0;
}
.theme--light .userchanger-adaptive-text {
    color: rgba(0, 0, 0, .87);
}
.theme--dark .userchanger-adaptive-text {
    color: rgba(255, 255, 255, .87);
}
.players-activity-scroll {
    max-height: 50vh;
    overflow-y: auto;
}
</style>
